import { useBackend } from '@cdab/scania/qpr/contexts/backend-provider'
import type {
  CreateADHLinkChatMessageData,
  UploadProgress
} from '@cdab/scania/qpr/interactor'
import type { FileData, UserFile } from '@cdab/scania/qpr/schema'

import { action } from 'mobx'
import { useState } from 'react'

export type UploadedChatMessageFile = FileData & {
  local_file_url: string
}

export type CreateChatMessageFileState =
  | {
      status: 'success'
      chatMessageId: number
    }
  | {
      status: 'error'
    }
  | {
      status: 'uploading'
      progress: number
    }
  | {
      status: 'none'
    }

export function useCreateChatMessageWithFile() {
  const [submitState, setSubmitState] = useState<CreateChatMessageFileState>({
    status: 'none'
  })

  const [uploadedFiles, setUploadedFiles] = useState<UploadedChatMessageFile[]>(
    []
  )
  const client = useBackend()

  const createChatMessageWithoutFile = async (
    data: CreateADHLinkChatMessageData
  ) => {
    const createPromise = client.ADHService.CreateADHLinkChatMessage({
      ...data
    })

    const [createResult] = await Promise.allSettled([createPromise])

    if (createResult.status === 'rejected') {
      setSubmitState({
        status: 'error'
      })
      return
    }

    setSubmitState({
      status: 'success',
      chatMessageId: createResult.value
    })
  }

  const uploadFileAndCreateChatMessage = async (
    data: CreateADHLinkChatMessageData,
    chatMessageFile: UserFile
  ) => {
    const uploadPromise = client.ADHService.UploadChatFile(
      chatMessageFile.file,
      action((progressEvent: UploadProgress) => {
        if (typeof progressEvent.progress === 'undefined') return
        setSubmitState({
          status: 'uploading',
          progress: progressEvent.progress
        })
      })
    )

    const createPromise = client.ADHService.CreateADHLinkChatMessage({
      ...data,
      fileId: Number.parseInt((await uploadPromise).fileData?.id ?? '0')
    })

    const [uploadResult, createResult] = await Promise.allSettled([
      uploadPromise,
      createPromise
    ])

    return { uploadResult, createResult }
  }

  const handleClickUploadAndCreateChatMessage = async (
    data: CreateADHLinkChatMessageData,
    chatMessageFile: UserFile | undefined
  ) => {
    if (!chatMessageFile) {
      createChatMessageWithoutFile(data)

      return
    }

    setSubmitState({
      status: 'uploading',
      progress: 0
    })

    const { uploadResult, createResult } = await uploadFileAndCreateChatMessage(
      data,
      chatMessageFile
    )

    if (uploadResult.status === 'rejected') {
      setSubmitState({
        status: 'error'
      })
      return
    }

    if (
      createResult.status === 'rejected' &&
      uploadResult.status === 'fulfilled'
    ) {
      const fileId = Number.parseInt(uploadResult.value.fileData?.id ?? '0')
      if (fileId > 0) {
        await client.ADHService.DeleteChatFile(fileId)
      }
      setSubmitState({
        status: 'error'
      })
      return
    }

    if (uploadResult.value.success && uploadResult.value.fileData) {
      const newUploadedFiles = uploadedFiles
      newUploadedFiles.push({
        ...uploadResult.value.fileData,
        local_file_url: chatMessageFile.url
      })

      setUploadedFiles(newUploadedFiles)
    }

    if (createResult.status === 'fulfilled') {
      setSubmitState({
        status: 'success',
        chatMessageId: createResult.value
      })
    } else {
      setSubmitState({
        status: 'error'
      })
    }
  }

  return [
    submitState,
    uploadedFiles,
    handleClickUploadAndCreateChatMessage,
    setSubmitState
  ] as const
}
